import { useCallback, useState } from "react";
import { useMsal } from "@azure/msal-react";
import urlconfig from "../config/urlconfig";
import { getApiToken } from "../authUtils";

// Data for the Sales, Purchase and Analytics dashboards, read from the backend's shared dashboard
// snapshot instead of downloading every cost sheet with all its collections.

const get = async (instance, account, path) => {
  const token = await getApiToken(instance, account);
  const res = await fetch(`${urlconfig.backend_api_base_url}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Request failed (HTTP ${res.status})`);
  return res.json();
};

const EMPTY_PAGE = { content: [], totalPages: 0, totalElements: 0, number: 0 };

/**
 * What the Sales and Purchase dashboards used from ItemsContext, with the same names, arguments and
 * data, so their own filtering and calculations run unchanged:
 *  - refreshItems(search, page, size): a page of cost sheets; with size 1000000 (used while filtering)
 *    every matching cost sheet, as the raw inputs those calculations read, in the same order.
 *  - fetchAllItemsForDeliveryAnalytics(): every cost sheet's raw inputs, into deliveryItems, for the cards.
 */
export const useInvoiceDashboardData = () => {
  const { instance, accounts } = useMsal();
  const account = accounts?.[0] || null;
  const [items, setItems] = useState(EMPTY_PAGE);
  const [deliveryItems, setDeliveryItems] = useState(EMPTY_PAGE);

  const refreshItems = useCallback(async (search = '', page = 0, size = 10) => {
    try {
      if (size >= 1000000) {
        const all = await get(instance, account, `/api/v2/dashboards/invoice-orders?search=${encodeURIComponent(search)}`);
        setItems({ content: all, totalPages: 1, totalElements: all.length, number: 0 });
      } else {
        setItems(await get(instance, account, `/api/v2/items?search=${encodeURIComponent(search)}&page=${page}&size=${size}`));
      }
    } catch (err) {
      console.error('Error fetching items:', err);
    }
  }, [instance, account]);

  const fetchAllItemsForDeliveryAnalytics = useCallback(async () => {
    try {
      const all = await get(instance, account, '/api/v2/dashboards/invoice-orders?search=');
      setDeliveryItems({ content: all, totalPages: 1, totalElements: all.length, number: 0 });
    } catch (err) {
      console.error('Error fetching all cost sheets for the cards:', err);
    }
  }, [instance, account]);

  return { items, refreshItems, deliveryItems, fetchAllItemsForDeliveryAnalytics };
};

/** Every dated cost sheet with just the fields the analytics charts use */
export const fetchAnalyticsOrders = (instance, account) =>
  get(instance, account, "/api/v2/dashboards/analytics");

/** The full cost sheet, for the upload and view dialogs */
export const fetchCostSheet = (instance, account, id) =>
  get(instance, account, `/api/v2/items/${encodeURIComponent(id)}`);
